import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { CalendarDays, Loader2 } from 'lucide-react'
import { handleOutlookCalendarCallback } from '@/lib/api'

const OutlookCalendarCallback = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState<any>(null)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get('code')
    const state = queryParams.get('state')
    const errorParam = queryParams.get('error')
    const errorDescription = queryParams.get('error_description')

    const processOutlookCalendarCallback = async () => {
      if (errorParam) {
        console.error('Outlook Calendar authorization error:', errorParam, errorDescription)
        setError(errorDescription || errorParam)
        setLoading(false)
        return
      }

      if (!code) {
        setError('No authorization code received from Outlook Calendar')
        setLoading(false)
        return
      }

      try {
        const response = await handleOutlookCalendarCallback({
          code,
          state: state || undefined,
        })

        console.log('Outlook Calendar callback response:', response)
        setResult(response)
      } catch (err: any) {
        console.error('Error processing Outlook Calendar callback:', err)
        setError(err.message || 'An error occurred while processing your request')
      } finally {
        setLoading(false)
      }
    }

    processOutlookCalendarCallback()
  }, [location.search, navigate])

  if (loading) {
    return (
      <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
        <Card variant="dark" className="overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-[#0078D4] p-4 rounded-full">
                <CalendarDays className="h-8 w-8 text-[#FFFFFF]" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              Processing Outlook Calendar Authorization
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we exchange the Outlook authorization code and save your token...
            </p>
            <Loader2 className="h-6 w-6 animate-spin text-[#4d7c0f] mx-auto" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
        <Card variant="dark" className="overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-[#0078D4] p-4 rounded-full">
                <CalendarDays className="h-8 w-8 text-[#FFFFFF]" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              Outlook Calendar Authorization Error
            </h2>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-x-4">
              <Link
                to="/connect/outlook-calendar"
                className="inline-block bg-[#C0FD72] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
              >
                Try Again
              </Link>
              <Link
                to="/calendar-setup"
                className="inline-block text-gray-600 hover:text-foreground px-4 py-2 transition-colors"
              >
                Back to Calendar Setup
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (result) {
    return (
      <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
        <Card variant="dark" className="overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-[#0078D4] p-4 rounded-full">
                <CalendarDays className="h-8 w-8 text-[#FFFFFF]" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              Outlook Calendar OAuth Response Received
            </h2>
            <p className="text-gray-600 mb-4">
              Microsoft redirected here first, then the frontend sent the code to the backend and
              the token was saved successfully. You can now test reading Outlook events.
            </p>
            <div className="rounded-xl bg-black/20 border border-foreground/10 p-4 text-left mb-4">
              <pre className="text-xs text-green-700 whitespace-pre-wrap break-all">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
            <div className="space-x-4">
              <button
                onClick={() => navigate('/connect/outlook-calendar?loadEvents=1')}
                className="inline-block bg-[#C0FD72] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
              >
                View Outlook events
              </button>
              <button
                onClick={() => navigate('/calendar-setup')}
                className="inline-block text-gray-600 hover:text-foreground px-4 py-2 transition-colors"
              >
                Back to Calendar Setup
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return null
}

export default OutlookCalendarCallback
